<template>
  <slot></slot>
</template>

<script setup lang="ts">
import { ref, provide, onMounted, watch, computed, type ComputedRef } from "vue";

type Theme = "dark" | "light";

const theme = ref<Theme>("light");
const isInitialized = ref(false);

const isDarkMode = computed(() => theme.value === "dark");

const toggleTheme = () => {
  theme.value = theme.value === "light" ? "dark" : "light";
};

onMounted(() => {
  const savedTheme = localStorage.getItem("theme") as null | Theme;
  const initialTheme = savedTheme || "light"; // Default to light theme

  theme.value = initialTheme;
  isInitialized.value = true;
});

watch([theme, isInitialized], ([newTheme, newIsInitialized]) => {
  if (newIsInitialized) {
    localStorage.setItem("theme", newTheme);
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }
});

provide("theme", {
  isDarkMode,
  toggleTheme,
});
</script>

<script lang="ts">
import { inject } from "vue";

export function useTheme() {
  const theme = inject<{ isDarkMode: ComputedRef<boolean>; toggleTheme: () => void }>("theme");
  if (!theme) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return theme;
}
</script>
